function safeText(value = "") {
  return String(value ?? "").trim();
}

function positiveInteger(value) {
  const number = Math.floor(Number(value));

  return Number.isFinite(number) && number > 0
    ? number
    : 0;
}

/**
 * Resolve a usable image URL from any supported
 * image shape.
 */
export function resolveImageCandidate(image) {
  if (typeof image === "string") {
    return safeText(image);
  }

  if (!image || typeof image !== "object") {
    return "";
  }

  const candidates = [
    image.url,
    image.secure_url,
    image.secureUrl,
    image.src,
    image.image,
    image.imageUrl,
    image.defaultImage,
    image.defaultImageUrl,
    image.featuredImage,
    image.featuredImageUrl,
    image.productImage,
    image.productImageUrl,
    image.colourwayImage,
    image.cover,
    image.thumbnail,
    image.thumbnailUrl,
  ];

  for (const candidate of candidates) {
    if (
      candidate &&
      candidate !== image
    ) {
      const resolved =
        resolveImageCandidate(candidate);

      if (resolved) {
        return resolved;
      }
    }
  }

  return "";
}

/**
 * Resolve the original/master image if the API
 * provides one explicitly.
 *
 * Falls back to the normal image URL.
 */
function resolveMasterImageCandidate(image) {
  if (typeof image === "string") {
    return safeText(image);
  }

  if (!image || typeof image !== "object") {
    return "";
  }

  const masterCandidates = [
    image.masterUrl,
    image.master_url,

    image.originalUrl,
    image.original_url,

    image.original,

    image.master,

    image.sourceUrl,
    image.source_url,

    image.fullUrl,
    image.full_url,

    image.fullSizeUrl,
    image.full_size_url,
  ];

  for (const candidate of masterCandidates) {
    const resolved =
      resolveImageCandidate(candidate);

    if (resolved) {
      return resolved;
    }
  }

  return resolveImageCandidate(image);
}

function normalizeDerivative(image) {
  if (
    !image ||
    typeof image !== "object"
  ) {
    return null;
  }

  const url =
    resolveImageCandidate(image);

  const width =
    positiveInteger(image.width);

  const height =
    positiveInteger(image.height);

  if (
    !url ||
    !width ||
    !height
  ) {
    return null;
  }

  const bytes = Math.max(
    0,
    Math.floor(
      Number(image.bytes) || 0,
    ),
  );

  return {
    ...image,

    url,

    public_id:
      safeText(image.public_id),

    width,

    height,

    bytes,
  };
}

function normalizeVariants(variants) {
  if (
    !variants ||
    typeof variants !== "object"
  ) {
    return undefined;
  }

  const normalizeList = (
    images,
  ) => {
    const seen = new Set();

    return (
      Array.isArray(images)
        ? images
        : []
    )
      .map(
        normalizeDerivative,
      )
      .filter((image) => {
        if (!image) {
          return false;
        }

        /*
         * One derivative per width
         * inside each format.
         */
        if (
          seen.has(image.width)
        ) {
          return false;
        }

        seen.add(image.width);

        return true;
      })
      .sort(
        (a, b) =>
          a.width - b.width,
      );
  };

  const normalized = {
    avif: normalizeList(
      variants.avif,
    ),

    webp: normalizeList(
      variants.webp,
    ),
  };

  return (
    normalized.avif.length ||
    normalized.webp.length
  )
    ? normalized
    : undefined;
}

/**
 * Normalize an image without discarding
 * existing metadata.
 *
 * The normal `url` remains the standard image.
 * `masterUrl` is retained separately when the
 * API provides a genuine original/master.
 */
export function normalizeProductImage(
  image,
  fallback = "",
) {
  const standardUrl =
    resolveImageCandidate(image);

  const fallbackUrl =
    safeText(fallback);

  const url =
    standardUrl ||
    fallbackUrl;

  if (!url) {
    return null;
  }

  if (
    !image ||
    typeof image !== "object"
  ) {
    return {
      url,

      masterUrl: url,

      public_id: "",

      alt: "",

      caption: "",
    };
  }

  const normalized = {
    ...image,

    url,

    public_id:
      safeText(
        image.public_id,
      ),

    alt:
      safeText(
        image.alt,
      ),

    caption:
      safeText(
        image.caption,
      ),
  };

  const masterUrl =
    resolveMasterImageCandidate(
      image,
    );

  const width =
    positiveInteger(
      image.width,
    );

  const height =
    positiveInteger(
      image.height,
    );

  const bytes = Math.max(
    0,
    Math.floor(
      Number(
        image.bytes,
      ) || 0,
    ),
  );

  const format =
    safeText(
      image.format,
    ).toLowerCase();

  const variants =
    normalizeVariants(
      image.variants,
    );

  if (masterUrl) {
    normalized.masterUrl =
      masterUrl;
  }

  if (width) {
    normalized.width =
      width;
  }

  if (height) {
    normalized.height =
      height;
  }

  if (bytes) {
    normalized.bytes =
      bytes;
  }

  if (format) {
    normalized.format =
      format;
  }

  if (variants) {
    normalized.variants =
      variants;
  }

  return normalized;
}

function collectProductImageCandidates(
  product = {},
) {
  const images =
    Array.isArray(
      product?.images,
    )
      ? product.images
      : [];

  const variants =
    Array.isArray(
      product?.variants,
    )
      ? product.variants
      : [];

  const options =
    Array.isArray(
      product?.purchasableOptions,
    )
      ? product.purchasableOptions
      : [];

  const productCandidates = [
    /*
     * Flat URL fields from
     * server serializers.
     */
    product?.defaultImage,
    product?.image,
    product?.imageUrl,
    product?.featuredImage,
    product?.productImage,

    /*
     * Legacy / colourway aliases.
     */
    product?.colourwayImage,
    product?.cover,

    /*
     * Product image collection.
     */
    images[0],
    ...images,
  ];

  const productCandidateUrls =
    new Set(
      productCandidates
        .map(
          resolveImageCandidate,
        )
        .filter(Boolean),
    );

  const nonProductCandidate = (
    candidate,
  ) => {
    const url =
      resolveImageCandidate(
        candidate,
      );

    return (
      url &&
      !productCandidateUrls.has(
        url,
      )
    );
  };

  return [
    /*
     * Variant-level images.
     */
    ...variants.flatMap(
      (variant) => {
        const variantImages =
          Array.isArray(
            variant?.images,
          )
            ? variant.images
            : [];

        return [
          variantImages[0],

          ...variantImages,

          ...[
            variant?.image,
            variant?.imageUrl,
            variant?.defaultImage,
            variant?.defaultImageUrl,
            variant?.featuredImage,
            variant?.productImage,
          ].filter(
            nonProductCandidate,
          ),
        ];
      },
    ),

    /*
     * Purchasable-option images.
     */
    ...options.flatMap(
      (option) => {
        const optionImages =
          Array.isArray(
            option?.images,
          )
            ? option.images
            : [];

        return [
          optionImages[0],

          ...optionImages,

          ...[
            option?.image,
            option?.imageUrl,
            option?.defaultImage,
            option?.defaultImageUrl,
          ].filter(
            nonProductCandidate,
          ),
        ];
      },
    ),

    ...productCandidates,
  ];
}

/**
 * Returns the best structured image object for
 * normal catalogue/PDP rendering.
 */
export function getProductImage(
  product = {},
  fallback = "",
) {
  const candidates =
    collectProductImageCandidates(
      product,
    );

  const selectedUrl =
    candidates
      .map(
        resolveImageCandidate,
      )
      .find(Boolean);

  const url =
    selectedUrl ||
    safeText(fallback);

  if (!url) {
    return null;
  }

  const matchingImages =
    candidates
      .map((candidate) =>
        normalizeProductImage(
          candidate,
        ),
      )
      .filter(
        (image) =>
          image?.url === url,
      );

  return (
    matchingImages.find(
      (image) =>
        image.variants,
    ) ||
    matchingImages.find(
      (image) =>
        image.width &&
        image.height,
    ) ||
    matchingImages[0] ||
    normalizeProductImage(
      url,
    )
  );
}

/**
 * Build responsive srcSet from derivatives.
 *
 * These are intended for the normal catalogue
 * and PDP views — not the full-screen master.
 */
export function buildProductImageSrcSet(
  image,
  format,
) {
  const normalized =
    normalizeProductImage(
      image,
    );

  const sources =
    normalized
      ?.variants
      ?.[format];

  if (
    !Array.isArray(sources)
  ) {
    return "";
  }

  return sources
    .filter(
      (source) =>
        source.url &&
        source.width,
    )
    .map(
      (source) =>
        `${source.url} ${source.width}w`,
    )
    .join(", ");
}

export function getProductImageSourceList(
  image,
  format,
) {
  const normalized =
    normalizeProductImage(
      image,
    );

  const sources =
    normalized
      ?.variants
      ?.[format];

  if (
    !Array.isArray(sources)
  ) {
    return [];
  }

  return sources
    .filter(
      (source) =>
        source.url &&
        source.width &&
        source.height,
    )
    .map((source) => ({
      src: source.url,
      width: source.width,
      height: source.height,
    }));
}

export function buildProductImageLightboxSlide(
  image,
  {
    alt = "",
    title = "",
    description = "",
  } = {},
) {
  const normalized =
    normalizeProductImage(
      image,
    );

  if (!normalized) {
    return null;
  }

  const slide = {
    src:
      normalized.masterUrl ||
      normalized.url,
    alt:
      safeText(alt) ||
      normalized.alt ||
      "Image",
    title:
      safeText(title),
    description:
      safeText(description) ||
      normalized.caption ||
      "",
  };

  if (normalized.width) {
    slide.width =
      normalized.width;
  }

  if (normalized.height) {
    slide.height =
      normalized.height;
  }

  const srcSet =
    getProductImageSourceList(
      normalized,
      "webp",
    );

  if (srcSet.length) {
    slide.srcSet =
      srcSet;
  }

  return slide;
}

/**
 * Return the image used by View Larger.
 *
 * Default behaviour:
 * use the genuine master/original URL.
 *
 * We do NOT automatically choose a derivative
 * for the lightbox.
 *
 * If master=false is requested, return the
 * largest available modern derivative.
 */
export function getLargestProductImageUrl(
  image,
  {
    master = true,
  } = {},
) {
  const normalized =
    normalizeProductImage(
      image,
    );

  if (!normalized) {
    return "";
  }

  if (master) {
    return (
      normalized.masterUrl ||
      normalized.url ||
      ""
    );
  }

  const modernSources = [
    ...(
      normalized
        .variants
        ?.webp || []
    ),

    ...(
      normalized
        .variants
        ?.avif || []
    ),
  ].sort(
    (a, b) =>
      b.width - a.width,
  );

  return (
    modernSources[0]?.url ||
    normalized.masterUrl ||
    normalized.url ||
    ""
  );
}

export function getProductImageUrl(
  product = {},
  fallback = "",
) {
  return (
    getProductImage(
      product,
      fallback,
    )?.url ||
    safeText(fallback)
  );
}
